/**
 * useOnboardingState Hook
 *
 * Manages onboarding wizard state and related operations.
 * Handles visibility, product fetching, test data population, and completion.
 *
 * IMPORTANT: This hook waits for App Bridge readiness before making API calls.
 * The `isReady` state indicates when authenticated operations are available.
 *
 * Supports two modes:
 * 1. Initial onboarding (shown automatically when not completed)
 * 2. Re-opened wizard from menu (for adding more products)
 *
 * @example
 * ```tsx
 * const onboarding = useOnboardingState({
 *   initialData: loaderData.onboardingData,
 *   revalidate: revalidator.revalidate,
 *   toast,
 * });
 *
 * // Initial onboarding wizard
 * {onboarding.shouldShowOnboarding && (
 *   <OnboardingWizard
 *     products={onboarding.products}
 *     onComplete={onboarding.handleComplete}
 *     onSkip={onboarding.handleDismiss}
 *   />
 * )}
 *
 * // Menu action to re-open wizard
 * <Button onClick={onboarding.handleOpenWizard}>Setup Wizard</Button>
 * ```
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { authenticatedFetch } from '../../utils/authenticated-fetch';
import type { OnboardingData, WizardProductData, InventoryItemsResponse } from '../../types/dashboard';

// App Bridge readiness configuration
const MAX_WAIT_MS = 3000;
const POLL_INTERVAL_MS = 50;

/**
 * Toast helper interface
 */
interface ToastHelpers {
  success: (message: string) => void;
  error: (message: string) => void;
}

/**
 * API Response types
 */
interface CompleteOnboardingResponse {
  success: boolean;
  productsCount?: number;
  error?: string;
}

/**
 * Input parameters for useOnboardingState hook
 */
export interface UseOnboardingStateInput {
  /** Initial onboarding data from loader */
  initialData: OnboardingData;
  /** Whether there's an error state from loader */
  hasError?: boolean;
  /** Revalidate function to refresh loader data */
  revalidate: () => void;
  /** Toast helpers for notifications */
  toast: ToastHelpers;
  /** Callback when tab should switch after wizard completion */
  onSwitchToMonitoredProducts?: () => void;
}

/**
 * Return type for useOnboardingState hook
 */
export interface UseOnboardingStateReturn {
  // Readiness state
  isReady: boolean;

  // Visibility state
  showOnboarding: boolean;
  showWizardFromMenu: boolean;
  shouldShowOnboarding: boolean; // Computed: either initial or menu-triggered
  setShowOnboarding: (value: boolean) => void;

  // Product state
  products: WizardProductData[];
  isLoadingProducts: boolean;

  // Error state
  errorBanner: string | null;
  setErrorBanner: (message: string | null) => void;

  // Computed
  isOnboardingComplete: boolean;

  // Handlers
  handleDismiss: () => Promise<void>;
  handleComplete: (selectedProductIds: string[]) => Promise<void>;
  handleOpenWizard: () => Promise<void>;
  handleCancelWizard: () => void;
  fetchProducts: () => Promise<void>;
}

/**
 * Hook for managing onboarding wizard state
 *
 * Consolidates all wizard-related state and handlers to reduce
 * duplication in the main dashboard component.
 *
 * @param input - Initial data and dependencies
 * @returns Onboarding state and handlers
 */
export function useOnboardingState({
  initialData,
  hasError = false,
  revalidate,
  toast,
  onSwitchToMonitoredProducts,
}: UseOnboardingStateInput): UseOnboardingStateReturn {
  // App Bridge readiness state
  const [isReady, setIsReady] = useState(false);
  const attemptsRef = useRef(0);
  const maxAttempts = MAX_WAIT_MS / POLL_INTERVAL_MS;

  // Visibility state
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [showWizardFromMenu, setShowWizardFromMenu] = useState(false);

  // Product state
  const [products, setProducts] = useState<WizardProductData[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const hasFetchedProductsRef = useRef(false);

  // Error state
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // Computed
  const isOnboardingComplete = initialData.onboardingCompleted;

  // Computed: show wizard if initial onboarding OR re-opened from menu
  const shouldShowOnboarding = !hasError && ((showOnboarding && !isOnboardingComplete) || showWizardFromMenu);

  // Poll for App Bridge readiness before allowing API calls
  useEffect(() => {
    let isMounted = true;

    const checkAppBridge = async (): Promise<boolean> => {
      if (typeof window === 'undefined') {
        return false;
      }

      const shopify = window.shopify;
      if (!shopify?.idToken) {
        return false;
      }

      try {
        const token = await shopify.idToken();
        if (token && isMounted) {
          setIsReady(true);
          return true;
        }
      } catch (err) {
        // idToken() throws when not in proper Shopify context - expected
      }
      return false;
    };

    // Attempt check immediately
    checkAppBridge();

    // Poll for App Bridge availability
    const interval = setInterval(async () => {
      attemptsRef.current++;

      if (attemptsRef.current >= maxAttempts) {
        clearInterval(interval);
        return;
      }

      if (await checkAppBridge()) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [maxAttempts]);

  // Fetch products for onboarding wizard (requires App Bridge readiness)
  const fetchProducts = useCallback(async () => {
    // Don't fetch if App Bridge isn't ready or already fetching
    if (!isReady || hasFetchedProductsRef.current || isLoadingProducts) {
      return;
    }

    hasFetchedProductsRef.current = true;
    setIsLoadingProducts(true);

    try {
      const response = await authenticatedFetch('/api/onboarding/inventory-items');
      const data: InventoryItemsResponse = await response.json();

      if (data.success && data.data?.products) {
        setProducts(data.data.products);
      } else {
        setProducts([]);
      }
    } catch (error) {
      setErrorBanner('Failed to load products. You can still skip onboarding and configure products later.');
    } finally {
      setIsLoadingProducts(false);
    }
  }, [isReady, isLoadingProducts]);

  // Auto-fetch products when wizard should show AND App Bridge is ready
  useEffect(() => {
    if (isReady && shouldShowOnboarding && !hasFetchedProductsRef.current && !isLoadingProducts) {
      fetchProducts();
    }
  }, [isReady, shouldShowOnboarding, isLoadingProducts, fetchProducts]);

  // Track first alert view for onboarding progress (only when ready)
  useEffect(() => {
    if (isReady && initialData.hasAlerts && initialData.shopId) {
      authenticatedFetch('/api/onboarding/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: 'first_alert' }),
      }).catch(() => {
        // Alert view tracking failed - silent fail
      });
    }
  }, [isReady, initialData.hasAlerts, initialData.shopId]);

  // Dismiss onboarding handler
  const handleDismiss = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/onboarding/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        setShowOnboarding(false);
        toast.success('Onboarding checklist dismissed');
        revalidate();
      }
    } catch (error) {
      // Dismiss failed - silent fail
    }
  }, [toast, revalidate]);

  /**
   * Complete onboarding/wizard handler
   *
   * Used for both initial onboarding completion and wizard re-opened from menu.
   * Closes the appropriate modal based on which is currently shown.
   */
  const handleComplete = useCallback(async (selectedProductIds: string[]) => {
    try {
      const response = await authenticatedFetch('/api/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: selectedProductIds }),
      });

      const data: CompleteOnboardingResponse = await response.json();

      if (response.ok && data.success) {
        toast.success(`Successfully configured ${data.productsCount} products for monitoring`);

        // Close whichever wizard mode is active
        if (showWizardFromMenu) {
          setShowWizardFromMenu(false);
        } else {
          setShowOnboarding(false);
        }

        // Generate initial alerts (fire-and-forget)
        try {
          await authenticatedFetch('/api/alerts/generate', { method: 'POST' });
        } catch (alertError) {
          // Alert generation failed - silent fail
        }

        // Switch to Monitored Products tab after completion (if callback provided)
        onSwitchToMonitoredProducts?.();

        revalidate();
      } else {
        setErrorBanner(data.error || 'Failed to save product selections');
      }
    } catch (error) {
      setErrorBanner(error instanceof Error ? error.message : 'Failed to save product selections');
    }
  }, [showWizardFromMenu, toast, revalidate, onSwitchToMonitoredProducts]);

  /**
   * Open wizard from menu action
   *
   * Fetches products if not already loaded, then shows wizard.
   */
  const handleOpenWizard = useCallback(async () => {
    // Fetch products if not already loaded
    if (products.length === 0 && !isLoadingProducts) {
      setIsLoadingProducts(true);
      try {
        const response = await authenticatedFetch('/api/onboarding/inventory-items');
        const data: InventoryItemsResponse = await response.json();
        if (data.success && data.data?.products) {
          setProducts(data.data.products);
        }
      } catch (error) {
        // Product fetch failed - silent fail
      } finally {
        setIsLoadingProducts(false);
      }
    }
    setShowWizardFromMenu(true);
  }, [products.length, isLoadingProducts]);

  /**
   * Cancel wizard opened from menu
   */
  const handleCancelWizard = useCallback(() => {
    setShowWizardFromMenu(false);
  }, []);

  return {
    // Readiness state
    isReady,

    // Visibility state
    showOnboarding,
    showWizardFromMenu,
    shouldShowOnboarding,
    setShowOnboarding,

    // Product state
    products,
    isLoadingProducts,

    // Error state
    errorBanner,
    setErrorBanner,

    // Computed
    isOnboardingComplete,

    // Handlers
    handleDismiss,
    handleComplete,
    handleOpenWizard,
    handleCancelWizard,
    fetchProducts,
  };
}
